import React, { useCallback, useRef } from 'react';
import './file-input.scss';
import { useForm } from 'react-hook-form';
import { AppButton, AppButtonType, AppButtonVariant } from '../buttons/app-button';

export interface AppFileInputProps {
  buttonLabel: string;
  acceptedFile?: string;
  className?: string;
  onUpload: (base64Image: string) => void;
}

export const convertFileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file?.arrayBuffer) {
      reject();
    } else {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject;
      reader.readAsDataURL(file);
    }
  });

const checkMimeType = (fileList: FileList | null, accept?: string) => {
  if (!accept) return true;
  const matchMimeType = accept.replaceAll('.', '').split(', ').join('|');
  return Array.from(fileList ?? []).some((x) => x.type.match(matchMimeType));
};

export const AppFileInput = (props: AppFileInputProps) => {
  const { buttonLabel, acceptedFile, className, onUpload } = props;
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const { register } = useForm({});

  const addDocument = useCallback(() => {
    if (hiddenFileInput?.current !== null) {
      hiddenFileInput?.current.click();
    }
  }, [hiddenFileInput]);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      const isValidFormat = checkMimeType(fileList, acceptedFile);
      if (fileList && isValidFormat) {
        const base64 = await convertFileToBase64(fileList[0]);
        onUpload(base64);
      } else {
        alert('File type not supported');
      }
    },
    [acceptedFile, onUpload]
  );

  return (
    <div className='app-file-input'>
      <AppButton
        className={`app-file-input--button ${className || ''}`}
        type={AppButtonType.BUTTON}
        variant={AppButtonVariant.Primary}
        label={buttonLabel}
        onClick={addDocument}
      />
      <input
        id='fileInput'
        className={'app-file-input--input'}
        type='file'
        {...register('fileInput')}
        ref={hiddenFileInput}
        onChange={handleChange}
        accept={acceptedFile}
      />
    </div>
  );
};
